import { Flex } from '@abyss/web/ui/Flex';
import { IconSymbol } from '@abyss/web/ui/IconSymbol';
import find from 'lodash/find';
import React, { useContext } from 'react';

import { ConstantsLagoon } from '../../../../../../common/ConstantsLagoon';
import { PSXIcon } from '../../../../../../common/PSXIcon';
import { getFeatureFlag } from '../../../../../../common/Utils';
import { PortalContext } from '../../../../../../context/PortalContext';
import { useCoverageType } from '../../../../../../hooks/useCoverageType';
import { useFeatureFlag } from '../../../../../../hooks/useFeatureFlag';
import { useLagoon } from '../../../../../../hooks/useLagoon';
import { useSmartChoice } from '../../../../../../hooks/useSmartChoice';
import { StoreKeys } from '../../../../../../hooks/useStore/state';
import { useStore } from '../../../../../../hooks/useStore/useStore';
import { suppressCostEstimatesForPolicies } from '../../../../../../utils/featureSuppress';
import { capitalizeFirstLetterOfEachWord } from '../../../../../../utils/providerNames.utils';
import { getShowCost } from '../../../../../../utils/showCost.utils';
import { getCurrentMember } from '../../../../../../utils/user.utils';
import { replacePlaceholders } from '../../../CompareSmartChoice/Benefits';
import { LayoutGroupStyled } from '../../../CompareSmartChoice/CompareSmartChoice.styled';
import { Tier2Icon } from '../../../CompareSmartChoice/Tier2Icon';
import {
  IconWithText,
  PrintViewDivider,
  PrintViewProviderBoldText,
  PrintViewProviderDetailsXSText,
  Section,
} from './PrintExpandedCardStyle.styled';

export const PrintExpandedBenefitsCard = ({
  selectedAttributes,
  selectedProviders,
  // isTieredPlan,
}) => {
  const isSmartChoice = useSmartChoice();
  const featureFlags = useLagoon('feature-flags')();
  const [costEnableFlag] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.COST_ENABLED,
  ]);
  const { portalData } = useContext(PortalContext);
  const portalShowCost = getShowCost(portalData);
  const coverageType = useCoverageType();

  const suppressCostEstimates = suppressCostEstimatesForPolicies(
    coverageType,
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.COST_ESTIMATES,
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.COST_ESTIMATES_EMPIRE
  );
  const showTier2Badge: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_TIER_TWO_PROVIDER
  );

  const currentMember = getCurrentMember();
  const isTier1Plan = useStore(StoreKeys.TIER1_PLAN_FLAG);
  const isTier2Plan = currentMember?.isIFPTieredPlan && showTier2Badge;
  const isCostEfficiencyRating = (item) =>
    item?.recommendationDetails?.benefits?.costEfficiencyRating;

  const lagoonData = useLagoon('ui-messaging')();
  const in_network_provider = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_IN_NETWORK,
  })?.message;
  const out_network_provider = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_OUT_OF_NETWORK,
  })?.message;
  const provider_tier = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_PROVIDER_TIER,
  })?.message;
  const not_provider_tier = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_NOT_TIERED_PROVIDER,
  })?.message;
  const provider_tier_2 = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_TIER_2_PROVIDER,
  })?.message;
  const preferred_provider = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.SMART_CHOICE_BENEFITS_BREAKDOWN
      .BENIFITS_BREAKDOWN_PREFERRED_PROVIDER,
  })?.message;
  const not_preferred_provider = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_NOT_PREFERRED_PROVIDER,
  })?.message;
  const aco_provider_text = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_ACO_PROVIDER_TEXT,
  })?.message;
  const not_aco_provider = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_NOT_ACO_PROVIDER,
  })?.message;
  const compare_below_average_cost = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_BELOW_AVERAGE_COST,
  })?.message;
  const compare_average_cost = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_AVERAGE_COST,
  })?.message;
  const compare_above_average_cost = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_ABOVE_AVERAGE_COST,
  })?.message;
  const compare_cost_unavailable = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_COST_NOT_AVAILABLE,
  })?.message;
  const compare_below_average_cost_care = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_BELOW_AVERAGE_COST_CARE,
  })?.message;
  const compare_average_cost_care = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_AVERAGE_COST_CARE,
  })?.message;
  const compare_above_average_cost_care = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_ABOVE_AVERAGE_COST_CARE,
  })?.message;
  const compare_cost_care_unavailable = find(lagoonData, {
    key: ConstantsLagoon.UI_MESSAGING.COMPARE.COMPARE_COST_CARE_NOT_AVAILABLE,
  })?.message;

  const acoNameDisplay = (item) =>
    item?.acoProvider?.acoNetworkDescription?.join(', ') ?? '';

  const acoName = (item) =>
    capitalizeFirstLetterOfEachWord(acoNameDisplay(item));
  const location = (item) =>
    `${item?.address?.line}, ${item?.address?.city}, ${item?.address?.state}`;
  const acoText = (item) =>
    replacePlaceholders(aco_provider_text, {
      location: location(item),
      acoName: acoName(item),
    });
  // const isProviderTierDesignation = (item) =>
  //   item?.recommendationDetails?.benefits?.providerTierDesignation?.toLowerCase();
  const costEfficiencyRatingCodes = ['A', 'B', 'C', 'D'];

  const getCost = (cost: string | undefined) => {
    switch (cost) {
      case '$$$':
        return (
          <PrintViewProviderBoldText css={{ color: '#007000' }}>
            <PrintViewProviderBoldText css={{ color: '#0248AC' }}>
              $$$
            </PrintViewProviderBoldText>{' '}
            {compare_above_average_cost}
          </PrintViewProviderBoldText>
        );
      case '$$':
        return (
          <PrintViewProviderBoldText css={{ color: '#0248AC' }}>
            $$
            <PrintViewProviderBoldText css={{ color: '#929496' }}>
              $
            </PrintViewProviderBoldText>{' '}
            {compare_average_cost}
          </PrintViewProviderBoldText>
        );
      case '$':
        return (
          <PrintViewProviderBoldText css={{ color: '#007000' }}>
            $
            <PrintViewProviderBoldText css={{ color: '#929496' }}>
              $$
            </PrintViewProviderBoldText>{' '}
            {compare_below_average_cost}
          </PrintViewProviderBoldText>
        );
      default:
        return (
          <PrintViewProviderBoldText css={{ color: 'black' }}>
            {compare_cost_unavailable}
          </PrintViewProviderBoldText>
        );
    }
  };

  return (
    <Section>
      {(selectedAttributes?.find((record) => record?.name === 'networkStatus')
        ?.isChecked ||
        !isSmartChoice) && (
        <>
          <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
            {selectedProviders?.map((item, index) => (
              <LayoutGroupStyled css={{ alignItems: 'center' }} key={index}>
                <IconWithText>
                  <IconSymbol color="#007000" icon="done" size={20} />
                  <PrintViewProviderDetailsXSText>
                    {item?.networkStatus
                      ? in_network_provider
                      : out_network_provider}
                  </PrintViewProviderDetailsXSText>
                </IconWithText>
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>
          <LayoutGroupStyled
            css={{ '& > div': { width: '100%' } }}
            cssProps={{ minHeightAuto: true }}
            grow
          >
            {selectedProviders?.map((_, index) => (
              <PrintViewDivider key={index} orientation="horizontal" />
            ))}
          </LayoutGroupStyled>
        </>
      )}
      {selectedAttributes?.find((record) => record.name === 'tier')
        ?.isChecked &&
        isSmartChoice && (
          <>
            <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
              {selectedProviders?.map((item, index) => (
                <LayoutGroupStyled css={{ alignItems: 'center' }} key={index}>
                  {item?.isTieredProvider ? (
                    <Flex alignItems="center">
                      <PSXIcon
                        icon="Group 204976975"
                        size={16}
                        style={{ marginRight: '5px' }}
                      />
                      <PrintViewProviderBoldText>
                        {provider_tier}
                      </PrintViewProviderBoldText>
                    </Flex>
                  ) : (
                    <PrintViewProviderDetailsXSText>
                      {not_provider_tier}
                    </PrintViewProviderDetailsXSText>
                  )}
                </LayoutGroupStyled>
              ))}
            </LayoutGroupStyled>
            <LayoutGroupStyled
              css={{ '& > div': { width: '100%' } }}
              cssProps={{ minHeightAuto: true }}
              grow
            >
              {selectedProviders?.map((_, index) => (
                <PrintViewDivider key={index} orientation="horizontal" />
              ))}
            </LayoutGroupStyled>
          </>
        )}
      {selectedAttributes?.find(
        (record) =>
          record?.name === 'tierOneProvider' ||
          record?.name === 'tierOneAndTwoProvider'
      )?.isChecked &&
        !isSmartChoice &&
        (isTier1Plan || isTier2Plan) && (
          <>
            <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
              {selectedProviders?.map((item, index) => (
                <LayoutGroupStyled css={{ alignItems: 'center' }} key={index}>
                  {item?.isTieredProvider ? (
                    <IconWithText>
                      <PSXIcon
                        icon="Group 204976975"
                        size={16}
                        style={{ marginRight: '5px' }}
                      />
                      <PrintViewProviderBoldText>
                        {provider_tier}
                      </PrintViewProviderBoldText>
                    </IconWithText>
                  ) : isTier2Plan && item?.isTier2Provider ? (
                    <IconWithText>
                      <Tier2Icon size={'16px'} style={{ marginRight: '5px' }} />
                      <PrintViewProviderDetailsXSText>
                        {provider_tier_2}
                      </PrintViewProviderDetailsXSText>
                    </IconWithText>
                  ) : (
                    <PrintViewProviderDetailsXSText>
                      {not_provider_tier}
                    </PrintViewProviderDetailsXSText>
                  )}
                </LayoutGroupStyled>
              ))}
            </LayoutGroupStyled>
            <LayoutGroupStyled
              css={{ '& > div': { width: '100%' } }}
              cssProps={{ minHeightAuto: true }}
              grow
            >
              {selectedProviders?.map((_, index) => (
                <PrintViewDivider key={index} orientation="horizontal" />
              ))}
            </LayoutGroupStyled>
          </>
        )}
      {selectedAttributes?.find((record) => record.name === 'greenDiamond')
        ?.isChecked && (
        <>
          <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
            {selectedProviders?.map((item, index) => (
              <LayoutGroupStyled css={{ alignItems: 'center' }} key={index}>
                <PrintViewProviderDetailsXSText>
                  {item?.recommendationDetails?.benefits
                    ?.isGreenDiamondProvider ? (
                    <IconWithText>
                      <PSXIcon
                        icon={'green_diamond'}
                        size={16}
                        style={{ marginRight: '5px' }}
                      />
                      {preferred_provider}
                    </IconWithText>
                  ) : (
                    not_preferred_provider
                  )}
                </PrintViewProviderDetailsXSText>
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>
          <LayoutGroupStyled
            css={{ '& > div': { width: '100%' } }}
            cssProps={{ minHeightAuto: true }}
            grow
          >
            {selectedProviders?.map((_, index) => (
              <PrintViewDivider key={index} orientation="horizontal" />
            ))}
          </LayoutGroupStyled>
        </>
      )}

      {selectedAttributes?.find((record) => record.name === 'acoAffiliation')
        ?.isChecked && (
        <>
          <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
            {selectedProviders?.map((item, index) => (
              <LayoutGroupStyled css={{ alignItems: 'center' }} key={index}>
                <PrintViewProviderDetailsXSText>
                  {acoNameDisplay(item) !== ''
                    ? acoText(item)
                    : not_aco_provider}
                </PrintViewProviderDetailsXSText>
              </LayoutGroupStyled>
            ))}
          </LayoutGroupStyled>
          <LayoutGroupStyled
            css={{ '& > div': { width: '100%' } }}
            cssProps={{ minHeightAuto: true }}
            grow
          >
            {selectedProviders?.map((_, index) => (
              <PrintViewDivider key={index} orientation="horizontal" />
            ))}
          </LayoutGroupStyled>
        </>
      )}
      {selectedAttributes?.find((record) => record.name === 'costIndicator')
        ?.isChecked &&
        portalShowCost &&
        costEnableFlag &&
        !suppressCostEstimates &&
        isSmartChoice && (
          <>
            <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
              {selectedProviders?.map((item, index) => (
                <LayoutGroupStyled css={{ alignItems: 'center' }} key={index}>
                  <Flex style={{ alignItems: 'center' }}>
                    {getCost(item.costIndicator)}
                  </Flex>
                </LayoutGroupStyled>
              ))}
            </LayoutGroupStyled>
            <LayoutGroupStyled
              css={{ '& > div': { width: '100%' } }}
              cssProps={{ minHeightAuto: true }}
              grow
            >
              {selectedProviders?.map((_, index) => (
                <PrintViewDivider key={index} orientation="horizontal" />
              ))}
            </LayoutGroupStyled>
          </>
        )}
      {selectedAttributes?.find((record) => record?.name === 'cost')
        ?.isChecked &&
        portalShowCost &&
        costEnableFlag &&
        !suppressCostEstimates &&
        !isSmartChoice && (
          <>
            <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
              {selectedProviders?.map((item, index) => (
                <LayoutGroupStyled css={{ alignItems: 'center' }} key={index}>
                  <Flex style={{ alignItems: 'center' }}>
                    {getCost(item.costIndicator)}
                  </Flex>
                </LayoutGroupStyled>
              ))}
            </LayoutGroupStyled>
            <LayoutGroupStyled
              css={{ '& > div': { width: '100%' } }}
              cssProps={{ minHeightAuto: true }}
              grow
            >
              {selectedProviders?.map((_, index) => (
                <PrintViewDivider key={index} orientation="horizontal" />
              ))}
            </LayoutGroupStyled>
          </>
        )}
      {selectedAttributes?.find((item) => item?.name === 'costEfficiency')
        ?.isChecked &&
        isSmartChoice && (
          <>
            <LayoutGroupStyled css={{ '& > div': { width: '100%' } }} grow>
              {selectedProviders?.map((item, index) => (
                <LayoutGroupStyled css={{ alignItems: 'center' }} key={index}>
                  {costEfficiencyRatingCodes.includes(
                    isCostEfficiencyRating(item)
                  ) && (
                    <PrintViewProviderDetailsXSText>
                      {compare_below_average_cost_care}
                    </PrintViewProviderDetailsXSText>
                  )}

                  {isCostEfficiencyRating(item) === 'E' && (
                    <PrintViewProviderDetailsXSText>
                      {compare_average_cost_care}
                    </PrintViewProviderDetailsXSText>
                  )}
                  {['F', 'G'].includes(isCostEfficiencyRating(item)) && (
                    <PrintViewProviderDetailsXSText>
                      {compare_above_average_cost_care}
                    </PrintViewProviderDetailsXSText>
                  )}
                  {isCostEfficiencyRating(item) === '' && (
                    <PrintViewProviderDetailsXSText>
                      {compare_cost_care_unavailable}
                    </PrintViewProviderDetailsXSText>
                  )}
                </LayoutGroupStyled>
              ))}
            </LayoutGroupStyled>
            {!isSmartChoice && (
              <LayoutGroupStyled
                css={{ '& > div': { width: '100%' } }}
                cssProps={{ minHeightAuto: true }}
                grow
              >
                {selectedProviders?.map((_, index) => (
                  <PrintViewDivider key={index} orientation="horizontal" />
                ))}
              </LayoutGroupStyled>
            )}
          </>
        )}
    </Section>
  );
};
